import React, { useEffect, useMemo, useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import bestLogo from '../assets/logo-best.jpeg';
import './Signup.css';

const roles = [
  {
    id: 'student',
    label: 'Pelajar',
    color: '#42A5F5',
    desc: 'Akses jadual, rekod kehadiran, semakan prestasi dan sokongan pelajar.',
  },
  {
    id: 'teacher',
    label: 'Guru IVC',
    color: '#66BB6A',
    desc: 'Akses jadual kelas, rekod kehadiran dan refleksi pengajaran.',
  },
  {
    id: 'coach',
    label: 'Jurulatih Play & Talk',
    color: '#FFA726',
    desc: 'Akses sesi Play & Talk, jadual aktiviti dan pemantauan program.',
  },
];

const gradeOptions = ['Tingkatan 1', 'Tingkatan 2', 'Tingkatan 3', 'Tingkatan 4', 'Tingkatan 5'];
const subjectOptions = ['Bahasa Melayu', 'Bahasa Inggeris', 'Matematik', 'Sejarah'];
const daerahOptions = ['Tanah Merah', 'Bachok', 'Gua Musang', 'Kuala Krai', 'Jeli'];
const terminalOptions = ['Terminal 1', 'Terminal 2'];

const Signup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    schoolId: '',
    schoolName: '',
    daerah: '',
    terminal: '',
    grade: '',
    subject: '',
    humanVerified: false,
    agreeTerms: false,
  });

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const schoolsRef = collection(db, 'schools');
        const querySnapshot = await getDocs(schoolsRef);
        const schoolsList = [];

        querySnapshot.forEach((item) => {
          schoolsList.push({ id: item.id, ...item.data() });
        });

        schoolsList.sort((a, b) => {
          if (a.daerah !== b.daerah) return a.daerah.localeCompare(b.daerah);
          if (a.terminal !== b.terminal) return a.terminal.localeCompare(b.terminal);
          return (a.name || '').localeCompare(b.name || '');
        });

        setSchools(schoolsList);
      } catch (err) {
        console.error('Error fetching schools:', err);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchSchools();
  }, []);

  const passwordChecks = useMemo(() => {
    const pw = formData.password || '';
    return {
      length: pw.length >= 8,
      upper: /[A-Z]/.test(pw),
      lower: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      symbol: /[^A-Za-z0-9]/.test(pw),
      noSpace: !/\s/.test(pw),
    };
  }, [formData.password]);

  const passwordScore = useMemo(
    () => Object.values(passwordChecks).reduce((acc, ok) => acc + (ok ? 1 : 0), 0),
    [passwordChecks]
  );

  const passwordStrength = useMemo(() => {
    if (passwordScore <= 2) return { label: 'Lemah', level: 'weak' };
    if (passwordScore <= 4) return { label: 'Sederhana', level: 'medium' };
    return { label: 'Kuat', level: 'strong' };
  }, [passwordScore]);

  const isPasswordValid = useMemo(() => {
    return (
      passwordChecks.length &&
      passwordChecks.upper &&
      passwordChecks.lower &&
      passwordChecks.number &&
      passwordChecks.symbol &&
      passwordChecks.noSpace
    );
  }, [passwordChecks]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSchoolSelect = (e) => {
    const schoolId = e.target.value;
    const selectedSchool = schools.find((school) => school.id === schoolId);

    if (!selectedSchool) {
      setFormData((prev) => ({
        ...prev,
        schoolId: '',
        schoolName: '',
        daerah: '',
        terminal: '',
      }));
      setError('');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      schoolId: selectedSchool.id,
      schoolName: selectedSchool.name,
      daerah: selectedSchool.daerah,
      terminal: selectedSchool.terminal,
    }));
    setError('');
  };

  const handleRoleSelect = (roleId) => {
    setFormData((prev) => ({
      ...prev,
      role: roleId,
      schoolId: '',
      schoolName: '',
      daerah: '',
      terminal: '',
      grade: '',
      subject: '',
      humanVerified: prev.humanVerified,
      agreeTerms: prev.agreeTerms,
    }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.role) {
      setError('Sila pilih peranan anda.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      setError('Sila isi semua maklumat.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Format e-mel tidak sah.');
      return false;
    }

    const phoneRegex = /^01[0-9]-?[0-9]{7,8}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      setError('Format nombor telefon tidak sah (contoh: 012-3456789).');
      return false;
    }

    if (!formData.password) {
      setError('Sila masukkan kata laluan.');
      return false;
    }

    if (!isPasswordValid) {
      setError('Kata laluan belum cukup kuat. Sila ikut senarai semakan yang diberi.');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Kata laluan tidak sepadan.');
      return false;
    }

    return true;
  };

  const validateStep3 = () => {
    if (formData.role === 'student') {
      if (!formData.schoolId || !formData.grade) {
        setError('Sila pilih sekolah dan tingkatan.');
        return false;
      }
    } else if (formData.role === 'teacher') {
      if (!formData.daerah || !formData.terminal || !formData.subject) {
        setError('Sila pilih daerah, terminal dan subjek.');
        return false;
      }
    } else if (formData.role === 'coach') {
      if (!formData.daerah || !formData.terminal) {
        setError('Sila pilih daerah dan terminal.');
        return false;
      }
    }

    if (!formData.humanVerified) {
      setError('Sila sahkan bahawa anda bukan robot.');
      return false;
    }

    if (!formData.agreeTerms) {
      setError('Sila bersetuju dengan terma dan syarat.');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    setError('');
    if (currentStep === 1 && validateStep1()) setCurrentStep(2);
    else if (currentStep === 2 && validateStep2()) setCurrentStep(3);
  };

  const handleBack = () => {
    setCurrentStep((step) => Math.max(1, step - 1));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    setError('');

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      try {
        await sendEmailVerification(user);
      } catch (verificationError) {
        console.warn('Email verification not sent:', verificationError);
      }

      const userData = {
        uid: user.uid,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: 'active',
        emailVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (formData.role === 'student') {
        userData.schoolId = formData.schoolId;
        userData.schoolName = formData.schoolName;
        userData.daerah = formData.daerah;
        userData.terminal = formData.terminal;
        userData.grade = formData.grade;
      } else if (formData.role === 'teacher') {
        userData.daerah = formData.daerah;
        userData.terminal = formData.terminal;
        userData.subject = formData.subject;
      } else if (formData.role === 'coach') {
        userData.daerah = formData.daerah;
        userData.terminal = formData.terminal;
      }

      await setDoc(doc(db, 'users', user.uid), userData);

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Signup error:', err);

      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('E-mel ini telah digunakan. Sila gunakan e-mel lain atau log masuk.');
          break;
        case 'auth/invalid-email':
          setError('Format e-mel tidak sah.');
          break;
        case 'auth/weak-password':
          setError('Kata laluan terlalu lemah. Gunakan kata laluan lebih kuat.');
          break;
        default:
          setError('Ralat berlaku semasa pendaftaran. Sila cuba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const PasswordChecklist = () => (
    <div className="pw-checklist">
      <div className="pw-check-title">Keperluan kata laluan</div>
      <ul>
        <li className={passwordChecks.length ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Minimum 8 aksara
        </li>
        <li className={passwordChecks.upper ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Huruf besar (A-Z)
        </li>
        <li className={passwordChecks.lower ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Huruf kecil (a-z)
        </li>
        <li className={passwordChecks.number ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Nombor (0-9)
        </li>
        <li className={passwordChecks.symbol ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Simbol seperti !@#
        </li>
        <li className={passwordChecks.noSpace ? 'ok' : ''}>
          <span className="dot" aria-hidden="true" />
          Tiada ruang kosong
        </li>
      </ul>
    </div>
  );

  if (success) {
    return (
      <div className="signup-container">
        <div className="signup-background">
          <div className="signup-bg-pattern" />
        </div>
        <div className="success-screen">
          <div className="success-icon-large" aria-hidden="true"><span /></div>
          <h1>Pendaftaran Berjaya!</h1>
          <p>Akaun anda telah berjaya didaftarkan.</p>
          <p className="success-note">Sila semak e-mel untuk pengesahan, kemudian log masuk.</p>
          <div className="success-timer">Mengalihkan ke halaman log masuk dalam 2 saat...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <div className="signup-background">
        <div className="signup-bg-pattern" />
      </div>
      <button type="button" className="signup-home-link" onClick={() => navigate('/')} aria-label="Laman Utama">
        <span aria-hidden="true" />
      </button>

      <div className="signup-content">
        <section className="signup-hero" aria-label="Penerangan pendaftaran BTOP Wellness">
          <div className="signup-hero-copy">
            <div className="signup-eyebrow">PENDAFTARAN PORTAL</div>
            <h2 className="signup-hero-title">Daftar akaun BEST 2.0.</h2>
            <p className="signup-hero-description">
              Pilih peranan dan lengkapkan maklumat supaya portal dapat memaparkan
              jadual, kehadiran dan sokongan yang sesuai untuk anda.
            </p>
          </div>

          <div className="signup-hero-visual" aria-hidden="true">
            <div className="signup-photo">
              <div className="signup-photo-overlay" />
              <div className="signup-product-preview">
                <div className="signup-preview-top">
                  <span>Aliran Pendaftaran</span>
                  <strong>3 Langkah</strong>
                </div>
                <div className="signup-preview-steps">
                  <span className="done">Peranan</span>
                  <span className="active">Profil</span>
                  <span>Lokasi</span>
                </div>
                <div className="signup-preview-card">
                  <span>Akses papan pemuka</span>
                  <strong>Tetapan ikut peranan</strong>
                  <p>Sekolah, terminal, subjek dan jadual disusun mengikut maklumat pengguna.</p>
                </div>
              </div>
              <div className="signup-float-card signup-float-top">
                <span className="float-kicker">PENDAFTARAN RINGKAS</span>
                <strong>Peranan / Profil / Lokasi</strong>
              </div>
              <div className="signup-float-card signup-float-bottom">
                <span className="float-pill">Pelajar</span>
                <span className="float-pill">Cikgu</span>
                <span className="float-pill">Jurulatih</span>
              </div>
            </div>

            <div className="signup-hero-strip">
              <div className="signup-metric">
                <span className="metric-number">3</span>
                <span className="metric-copy">Langkah ringkas untuk lengkapkan pendaftaran</span>
              </div>
              <div className="signup-metric">
                <span className="metric-number">Disahkan</span>
                <span className="metric-copy">Identiti dan butiran program lebih teratur</span>
              </div>
            </div>
          </div>
        </section>

        <div className="signup-card">
          <div className="signup-header">
            <div className="logo-small">
              <img src={bestLogo} alt="Logo BEST BTOP" />
            </div>
            <div>
              <h1>BTOP Wellness</h1>
              <p>Pendaftaran Akaun Baharu</p>
            </div>
          </div>

          <div className="progress-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <div className="step-circle">1</div>
              <span>Peranan</span>
            </div>

            <div className={`step-line ${currentStep > 1 ? 'filled' : ''}`} />

            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              <div className="step-circle">2</div>
              <span>Maklumat</span>
            </div>

            <div className={`step-line ${currentStep > 2 ? 'filled' : ''}`} />

            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-circle">3</div>
              <span>Lokasi</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="signup-form">
            {currentStep === 1 && (
              <div className="form-step">
                <h2>Pilih Peranan Anda</h2>
                <p className="step-description">Pilih akses yang paling sesuai dengan tugas anda dalam program.</p>

                <div className="role-selection-large">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      className={`role-card-large ${formData.role === role.id ? 'active' : ''}`}
                      onClick={() => handleRoleSelect(role.id)}
                      style={{
                        '--role-color': role.color,
                        borderColor: formData.role === role.id ? role.color : 'transparent',
                      }}
                    >
                      <span className={`role-icon-large role-icon-large-${role.id}`} aria-hidden="true"><span /></span>
                      <div className="role-info">
                        <h3>{role.label}</h3>
                        <p>{role.desc}</p>
                      </div>
                      {formData.role === role.id && <span className="role-check-large" aria-hidden="true" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="form-step">
                <h2>Maklumat Peribadi</h2>
                <p className="step-description">Isikan butiran yang akan digunakan untuk log masuk dan pengesahan akaun.</p>

                <div className="form-grid">
                  <div className="input-group full-width">
                    <label htmlFor="fullName">Nama Penuh *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Ahmad bin Abdullah"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="email">E-mel *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="nama@contoh.com"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="phone">No. Telefon *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="012-3456789"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="password">Kata Laluan *</label>
                    <div className="pw-field">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Min. 8 aksara + huruf besar/kecil + nombor + simbol"
                        required
                      />
                      <button
                        type="button"
                        className="pw-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'Sembunyikan kata laluan' : 'Tunjuk kata laluan'}
                        title={showPassword ? 'Sembunyi' : 'Tunjuk'}
                      >
                        {showPassword ? 'Sembunyi' : 'Tunjuk'}
                      </button>
                    </div>

                    <div className={`pw-meter ${passwordStrength.level}`}>
                      <div className="pw-meter-bar" style={{ width: `${(passwordScore / 6) * 100}%` }} />
                    </div>
                    <div className={`pw-meter-label ${passwordStrength.level}`}>
                      Kekuatan: <strong>{passwordStrength.label}</strong>
                    </div>

                    <PasswordChecklist />
                  </div>

                  <div className="input-group">
                    <label htmlFor="confirmPassword">Sahkan Kata Laluan *</label>
                    <div className="pw-field">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Masukkan semula"
                        required
                      />
                      <button
                        type="button"
                        className="pw-toggle"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        aria-label={showConfirmPassword ? 'Sembunyikan kata laluan pengesahan' : 'Tunjuk kata laluan pengesahan'}
                        title={showConfirmPassword ? 'Sembunyi' : 'Tunjuk'}
                      >
                        {showConfirmPassword ? 'Sembunyi' : 'Tunjuk'}
                      </button>
                    </div>

                    {!!formData.confirmPassword && (
                      <div className={`pw-match ${formData.password === formData.confirmPassword ? 'ok' : 'bad'}`}>
                        {formData.password === formData.confirmPassword
                          ? 'Kata laluan sepadan'
                          : 'Semak semula - kata laluan tidak sepadan'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="form-step">
                <h2>Maklumat Lokasi</h2>
                <p className="step-description">
                  {formData.role === 'student' && 'Pilih sekolah dan tingkatan untuk padanan jadual yang tepat.'}
                  {formData.role === 'teacher' && 'Tetapkan daerah, terminal dan subjek untuk paparan guru.'}
                  {formData.role === 'coach' && 'Tetapkan daerah dan terminal untuk akses sesi Play & Talk.'}
                </p>

                <div className="form-grid">
                  {formData.role === 'student' && (
                    <>
                      <div className="input-group full-width">
                        <label htmlFor="schoolId">Sekolah *</label>
                        {loadingSchools ? (
                          <div className="loading-text">Memuatkan senarai sekolah...</div>
                        ) : (
                          <select id="schoolId" name="schoolId" value={formData.schoolId} onChange={handleSchoolSelect} required>
                            <option value="">-- Pilih Sekolah --</option>
                            {schools.map((school) => (
                              <option key={school.id} value={school.id}>
                                {school.name} ({school.daerah} - {school.terminal})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {formData.schoolId && (
                        <div className="auto-filled-info">
                          <div className="info-item">
                            <span className="info-label">Daerah:</span>
                            <span className="info-value">{formData.daerah}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Terminal:</span>
                            <span className="info-value">{formData.terminal}</span>
                          </div>
                        </div>
                      )}

                      <div className="input-group">
                        <label htmlFor="grade">Tingkatan *</label>
                        <select id="grade" name="grade" value={formData.grade} onChange={handleInputChange} required>
                          <option value="">-- Pilih Tingkatan --</option>
                          {gradeOptions.map((grade) => (
                            <option key={grade} value={grade}>{grade}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {formData.role === 'teacher' && (
                    <>
                      <div className="input-group">
                        <label htmlFor="daerah">Daerah *</label>
                        <select id="daerah" name="daerah" value={formData.daerah} onChange={handleInputChange} required>
                          <option value="">-- Pilih Daerah --</option>
                          {daerahOptions.map((daerah) => (
                            <option key={daerah} value={daerah}>{daerah}</option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group">
                        <label htmlFor="terminal">Terminal *</label>
                        <select id="terminal" name="terminal" value={formData.terminal} onChange={handleInputChange} required>
                          <option value="">-- Pilih Terminal --</option>
                          {terminalOptions.map((terminal) => (
                            <option key={terminal} value={terminal}>{terminal}</option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group">
                        <label htmlFor="subject">Subjek *</label>
                        <select id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required>
                          <option value="">-- Pilih Subjek --</option>
                          {subjectOptions.map((subject) => (
                            <option key={subject} value={subject}>{subject}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {formData.role === 'coach' && (
                    <>
                      <div className="input-group">
                        <label htmlFor="daerah">Daerah *</label>
                        <select id="daerah" name="daerah" value={formData.daerah} onChange={handleInputChange} required>
                          <option value="">-- Pilih Daerah --</option>
                          {daerahOptions.map((daerah) => (
                            <option key={daerah} value={daerah}>{daerah}</option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group">
                        <label htmlFor="terminal">Terminal *</label>
                        <select id="terminal" name="terminal" value={formData.terminal} onChange={handleInputChange} required>
                          <option value="">-- Pilih Terminal --</option>
                          {terminalOptions.map((terminal) => (
                            <option key={terminal} value={terminal}>{terminal}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <div className="input-group full-width human-check-card">
                    <label className="human-check-label">
                      <input
                        type="checkbox"
                        name="humanVerified"
                        checked={formData.humanVerified}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="human-check-control" aria-hidden="true" />
                      <span className="human-check-copy">
                        <strong>Saya bukan robot</strong>
                        <small>Sahkan pendaftaran ini dibuat oleh pengguna sebenar.</small>
                      </span>
                    </label>
                  </div>

                  <div className="input-group full-width checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleInputChange}
                        required
                      />
                      <span>
                        Saya bersetuju dengan <a href="/terms" target="_blank" rel="noreferrer">Terma & Syarat</a> dan{' '}
                        <a href="/privacy" target="_blank" rel="noreferrer">Dasar Privasi</a>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="error-message">
                <span className="error-icon">!</span>
                <span>{error}</span>
              </div>
            )}

            <div className="form-navigation">
              {currentStep > 1 && (
                <button type="button" className="btn-secondary" onClick={handleBack}>Kembali</button>
              )}

              {currentStep < 3 ? (
                <button type="button" className="btn-primary" onClick={handleNext}>Seterusnya</button>
              ) : (
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner" />
                      <span>Mendaftar...</span>
                    </>
                  ) : (
                    'Daftar Akaun'
                  )}
                </button>
              )}
            </div>
          </form>

          <div className="signup-footer">
            Sudah ada akaun?{' '}
            <button type="button" className="link-button" onClick={() => navigate('/login')}>
              Log masuk di sini
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
